import { useEffect, useRef, useState } from 'react';
import { ImageIcon, X, Loader2 } from 'lucide-react';
import { api, ApiError, type UploadResponse } from '../lib/api';

const MAX_FILES = 3;
const MAX_BYTES = 3 * 1024 * 1024;
const ACCEPT_MIME = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];

export interface UploadedItem {
  token: string;
  previewUrl: string;
  name: string;
  size: number;
}

interface PhotoInputProps {
  value: UploadedItem[];
  onChange: (next: UploadedItem[]) => void;
  onError?: (message: string) => void;
}

export function PhotoInput({ value, onChange, onError }: PhotoInputProps) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    return () => {
      for (const item of value) URL.revokeObjectURL(item.previewUrl);
    };
    // rev on unmount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    const remaining = MAX_FILES - value.length;
    if (remaining <= 0) {
      onError?.('Максимум 3 файла');
      return;
    }
    const chosen = Array.from(files).slice(0, remaining);
    for (const file of chosen) {
      if (!ACCEPT_MIME.includes(file.type)) {
        onError?.(`Формат ${file.type || 'файла'} не поддерживается`);
        continue;
      }
      if (file.size > MAX_BYTES) {
        onError?.(`«${file.name}» больше 3 МБ`);
        continue;
      }
      setUploading(true);
      try {
        const uploaded: UploadResponse = await api.uploadFile(file);
        onChange([
          ...value,
          {
            token: uploaded.upload_token,
            previewUrl: URL.createObjectURL(file),
            name: file.name,
            size: uploaded.size,
          },
        ]);
      } catch (err) {
        const msg = err instanceof ApiError ? err.message : 'Не удалось загрузить';
        onError?.(msg);
      } finally {
        setUploading(false);
      }
    }
    if (fileRef.current) fileRef.current.value = '';
  }

  function removeAt(index: number) {
    const item = value[index];
    if (item) URL.revokeObjectURL(item.previewUrl);
    onChange(value.filter((_, i) => i !== index));
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-2">
        {value.map((item, i) => (
          <div
            key={item.token}
            className="relative h-20 w-20 overflow-hidden rounded-input border border-line"
          >
            <img
              src={item.previewUrl}
              alt={item.name}
              className="h-full w-full object-cover"
            />
            <button
              type="button"
              onClick={() => removeAt(i)}
              aria-label="Удалить"
              className="absolute right-1 top-1 grid h-5 w-5 place-items-center rounded-full bg-black/60 text-white transition-colors hover:bg-black/80"
            >
              <X size={12} />
            </button>
          </div>
        ))}
        {value.length < MAX_FILES && (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="grid h-20 w-20 place-items-center rounded-input border border-dashed border-line text-ink-muted transition-colors hover:border-ink-muted hover:bg-surface-muted disabled:cursor-not-allowed"
          >
            {uploading ? <Loader2 size={22} className="animate-spin" /> : <ImageIcon size={22} />}
          </button>
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        multiple
        accept={ACCEPT_MIME.join(',')}
        className="hidden"
        onChange={(e) => void handleFiles(e.target.files)}
      />
      <p className="text-[12px] text-ink-muted">
        До 3 файлов, PNG/JPG/WEBP/GIF, до 3 МБ каждый.
      </p>
    </div>
  );
}
